/**
 * Coach reactions and replies on standups and weekly summaries. Shared so
 * the queue's "needs a reply" rule is the same on the server and in the
 * browser after a coach reacts without reloading.
 */

export const CHECKIN_REACTIONS = [
  { key: 'thumbs_up', emoji: '👍', label: 'Thumbs up' },
  { key: 'clap', emoji: '👏', label: 'Clap' },
  { key: 'fire', emoji: '🔥', label: 'Fire' },
  { key: 'muscle', emoji: '💪', label: 'Strong' },
  { key: 'heart', emoji: '❤️', label: 'Heart' },
] as const;

export type CheckinReactionKey = (typeof CHECKIN_REACTIONS)[number]['key'];

export const CHECKIN_REACTION_KEYS = CHECKIN_REACTIONS.map((r) => r.key) as [
  CheckinReactionKey,
  ...CheckinReactionKey[],
];

export const MAX_CHECKIN_REPLY_LENGTH = 4000;

/** Matches the CHECK on checkin_summaries.body. */
export const MAX_CHECKIN_SUMMARY_LENGTH = 10000;

/** A reply's email waits this long, so the coach can still fix or delete it. */
export const CHECKIN_REPLY_EMAIL_DELAY_MINUTES = 10;

/** A reply older than this is never emailed. */
export const CHECKIN_REPLY_EMAIL_MAX_AGE_HOURS = 24;

/** Where coach emails for new standups and summaries go. */
export type CheckinSendTo = 'address' | 'coach';

/**
 * Worded to match the coach voice in the Agents Immersion Sprint build spec
 * (v5.3, section 2.4): acknowledge lightly and honestly, never solve the
 * problem in the reply, send a stuck student to their support thread with
 * the three-part question, and offer a call only after that.
 */
export const CHECKIN_QUICK_REPLIES = [
  { label: 'Thanks', text: 'Thanks for the update. Keep going.' },
  { label: 'Nice step', text: 'That’s a real step. Keep using it and see what it shows you.' },
  {
    label: 'Ask Claude',
    text: 'Take this to your support thread: say what you asked Claude, what it said, and what you tried.',
  },
  {
    label: 'Offer a call',
    text: 'If you’re still stuck after that, say so in your next standup and we’ll set up a call.',
  },
];

export type CheckinFeedbackReply = {
  id: UUID;
  /** Unset once the coach's account is deleted; the reply stays. */
  authorUserId?: UUID;
  authorName: string;
  body: string;
  createdAt: Date;
  updatedAt: Date;
  isEdited: boolean;
  emailSentAt?: Date;
  /** Set once the mailer has taken it, sent or not (a retired reply is never sent). */
  emailClaimedAt?: Date;
  studentSeenAt?: Date;
};

export type CheckinFeedbackReaction = {
  reaction: CheckinReactionKey;
  /** Unset once the coach's account is deleted; the reaction stays. */
  coachUserId?: UUID;
  coachName: string;
  createdAt: Date;
  studentSeenAt?: Date;
};

export type CheckinFeedback = {
  reactions: CheckinFeedbackReaction[];
  replies: CheckinFeedbackReply[];
};

export function emptyFeedback(): CheckinFeedback {
  return { reactions: [], replies: [] };
}

/** When a coach last acknowledged the item. Editing a reply doesn't count. */
export function lastAcknowledgedAt(feedback: CheckinFeedback): Date | undefined {
  let latest: Date | undefined;
  for (const item of [...feedback.reactions, ...feedback.replies]) {
    if (!latest || item.createdAt > latest) {
      latest = item.createdAt;
    }
  }
  return latest;
}

export function isUpdatedAfterReply(item: { updatedAt: Date; feedback: CheckinFeedback }) {
  const acknowledged = lastAcknowledgedAt(item.feedback);
  return !!acknowledged && item.updatedAt > acknowledged;
}

/**
 * A standup or summary needs a reply until any coach reacts or replies, and
 * again if the student changes it afterwards.
 */
export function needsReply(item: { updatedAt: Date; feedback: CheckinFeedback }): boolean {
  return !lastAcknowledgedAt(item.feedback) || isUpdatedAfterReply(item);
}

/** The first coach to acknowledge it, for "Acknowledged by Barb". */
export function firstAcknowledgedBy(feedback: CheckinFeedback): string | undefined {
  const all = [
    ...feedback.reactions.map((r) => ({ at: r.createdAt, name: r.coachName })),
    ...feedback.replies.map((r) => ({ at: r.createdAt, name: r.authorName })),
  ].sort((a, b) => a.at.getTime() - b.at.getTime());
  return all[0]?.name;
}

// A whole answer that says there's nothing to report. Matched against the
// entire answer, so "No idea how to deploy it" still counts as stuck.
const NOTHING_STUCK =
  /^(no|nope|none|nothing|n\/?a|not really|all good|no blockers?|nothing (yet|right now|so far|at the moment|today)|none (yet|so far|today))[\s.!]*$/i;

function saysSomething(answer: string) {
  const text = answer.trim();
  return !!text && !NOTHING_STUCK.test(text);
}

/**
 * The student wrote something real under a "stuck" or "blocking" prompt.
 * Matched on the prompt text because each course words its own prompts.
 */
export function isStuckStandup(answers: { prompt: string; answer: string }[]): boolean {
  return answers.some((a) => /stuck|block/i.test(a.prompt) && saysSomething(a.answer));
}

/** The "Where I could use a nudge" line of a pasted weekly summary, if any. */
export function summaryNudgeLine(body: string): string | undefined {
  const match = /^\s*\**\s*where i could use a nudge\s*\**\s*:\s*\**\s*(.*)$/im.exec(body);
  const line = match?.[1]?.trim();
  return line || undefined;
}

export function summaryAsksForNudge(body: string): boolean {
  const line = summaryNudgeLine(body);
  return !!line && saysSomething(line);
}

/**
 * A pasted weekly summary as label/value rows. The weekly review writes it as
 * "Built: …", "State: …" and so on; any line without a label stays a plain
 * row so nothing the student pasted is dropped.
 */
export function summaryRows(body: string) {
  return body
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = /^\**\s*([^:*]{2,40}?)\s*\**\s*:\s*\**\s*(.*)$/.exec(line);
      if (!match) {
        return { label: '', value: line, isNudge: false };
      }
      return { label: match[1], value: match[2], isNudge: /nudge/i.test(match[1]) };
    });
}
